import React from 'react';
import {Button, Form, Table} from "react-bootstrap";
import "./SangerQCHistoryTable.css"
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel, SortingState,
    useReactTable,
} from "@tanstack/react-table";

export interface SangerQCHistoryTableRow {
    uuid: string,
    description: string,
    userName: string,
    stage: string,
    slNumber: string,
    timestamp: string,
    status: string,
    errorMessage: string,
    s3URL: string,
    batchJobURL: string,
    deleted: boolean,
}

export const TanStackSangerQCHistoryTable: React.FC<{
    data: SangerQCHistoryTableRow[];
    columns: ColumnDef<SangerQCHistoryTableRow, any>[];
    onRowClick: (uuid: string) => void;
}> = ({data, columns, onRowClick}) => {
    // Initial sorting state - set to timestamp in descending order
    const [sorting, setSorting] = React.useState<SortingState>([
        {id: 'timestamp', desc: true}
    ])
    const [pageSize, setPageSize] = React.useState(5)
    const [pageIndex, setPageIndex] = React.useState(0)

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            pagination: {
                pageIndex,
                pageSize
            },
        },
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableSorting: true,
        manualPagination: false,
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({pageIndex, pageSize}) : updater
            setPageIndex(next.pageIndex)
            setPageSize(next.pageSize)
        }
    })

    const paginationSize = [5, 10, 20, 40]
    const noData = table.getRowModel().rows.length === 0

    return (
        <>
            <div className='table-responsive'>
                <Table bordered hover className={"sanger-qc-history-table"}>
                    <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                // Skip hidden columns
                                if (header.column.columnDef.meta?.hidden) return null

                                return (
                                    <th key={header.id}
                                        className={`sanger-qc-history-table-$header.column.id}`}
                                    >
                                        <div className='d-flex justify-content-between align-items-center w-100'>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </div>
                                        {/* Render filterComponent if defined */}
                                        {header.column.getCanFilter() && header.column.columnDef.meta?.filterComponent && (
                                            <div>
                                                {header.column.columnDef.meta.filterComponent({column: header.column})}
                                            </div>
                                        )}
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {noData ? (
                        <tr>
                            <td colSpan={table.getVisibleFlatColumns().filter(col => !col.columnDef.meta?.hidden).length}>
                                <h6 className="text-primary m-2">No data found!</h6>
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => onRowClick(row.original.uuid)}
                                style={{cursor: 'pointer'}}
                            >
                                {row.getVisibleCells().map(cell => {
                                    if (cell.column.columnDef.meta?.hidden) return null
                                    return (
                                        <td className="p-0 py-1 px-2" key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))
                    )}
                    </tbody>
                </Table>
            </div>

            <div className={"d-flex justify-content-between align-items-center mt-3"}>
                <div className={"d-flex align-items-center gap-2"}>
                    <Form.Select
                        size={"sm"}
                        style={{width: "auto"}}
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {paginationSize.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                    <span>
                        {`Showing rows ${
                            table.getFilteredRowModel().rows.length === 0
                                ? 0
                                : pageIndex * pageSize + 1
                        } to ${
                            Math.min((pageIndex + 1) * pageSize, table.getFilteredRowModel().rows.length)
                        } of ${table.getFilteredRowModel().rows.length}`}
                    </span>
                </div>

                <div className={"d-flex align-items-center gap-2"}>
                    <Button
                        onClick={() => setPageIndex(0)}
                        disabled={table.getState().pagination.pageIndex === 0}
                    >«</Button>
                    <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >‹</Button>
                    <span>Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
                    <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >›</Button>
                    <Button
                        onClick={() => setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >»</Button>
                </div>
            </div>
        </>
    )
}